import React from 'react';
import { Modal, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { formatDateAndTime, convertToUSD, isAdminView } from '../../../utils';

function CustomRow({ textLeft, textRight }) {
  if (textRight === undefined) return null;
  return (
    <tr>
      <td className="py-2" style={{ paddingLeft: '2rem' }}>
        {textLeft}
      </td>
      <td className="py-2">{textRight}</td>
    </tr>
  );
}

function PaymentInstrumentModal({ show, data, onHide }) {
  const { t } = useTranslation();

  return (
    <Modal
      centered
      id="payment-instrument-modal"
      backdrop="static"
      show={show}
      onHide={onHide}
      size="lg"
      aria-labelledby="payment-instrument-modal-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="payment-instrument-modal-title">
          {t('common:paymentMethods.modal.detail.title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0 pb-3">
        <Table responsive className="twoCol">
          <thead>
            <tr>
              <th className="py-2" style={{ paddingLeft: '2rem' }}>
                {t('common:labels.detail')}
              </th>
              <th className="py-2">{t('common:labels.data')}</th>
            </tr>
          </thead>
          <tbody>
            <CustomRow
              textLeft={t('common:paymentMethods.labels.method')}
              textRight={data.payment_method_type}
            />
            {data.payment_method_type === 'Bank Account' && (
              <>
                <CustomRow
                  textLeft={t('common:paymentMethods.labels.name')}
                  textRight={data.account_name}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.routing')}
                  textRight={data.routing_number}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.account')}
                  textRight={data.account_number}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.institutionName')}
                  textRight={data.institution_name}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.accountLinkStatus')}
                  textRight={data.account_link_status}
                />
                <CustomRow
                  textLeft={t('common:labels.active')}
                  textRight={t(`common:status.${data.active ? 'true' : 'false'}`)}
                />
              </>
            )}
            {(data.payment_method_type === 'Wallet' ||
              data.payment_method_type === 'Ledger Account') && (
              <>
                <CustomRow
                  textLeft={t('common:paymentMethods.labels.name')}
                  textRight={data.nickname || t('common:labels.na')}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.wallets.type.address')}
                  textRight={data.blockchain_address}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.wallets.type.walletId')}
                  textRight={data.payment_method_id}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.wallets.type.isDefault')}
                  textRight={t(`common:labels.${data.default ? 'yes' : 'no'}`)}
                />
                <CustomRow
                  textLeft={t('common:labels.active')}
                  textRight={t(`common:status.${!data.frozen ? 'true' : 'false'}`)}
                />
                <CustomRow
                  textLeft={
                    isAdminView()
                      ? t('common:transactions.labels.sila_balance')
                      : t('common:transactions.labels.balance')
                  }
                  textRight={
                    data.sila_balance !== undefined ? convertToUSD(data.sila_balance) : undefined
                  }
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.at_priority_bank')}
                  textRight={t(`common:labels.${data.at_priority_bank ? 'yes' : 'no'}`)}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.pb_balance')}
                  textRight={
                    data.pb_balance !== undefined ? convertToUSD(data.pb_balance) : undefined
                  }
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.pb_status')}
                  textRight={data.pb_status}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.pb_account_id')}
                  textRight={data.pb_account_id}
                />
              </>
            )}
            {data.payment_method_type === 'Virtual Account' && (
              <>
                <CustomRow
                  textLeft={t('common:paymentMethods.labels.name')}
                  textRight={data.virtual_account_name}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.account')}
                  textRight={data.account_number}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.routing')}
                  textRight={data.routing_number}
                />
                <CustomRow
                  textLeft={t('common:labels.active')}
                  textRight={t(`common:status.${data.active ? 'true' : 'false'}`)}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.virtualAccount.achCreditEnabled')}
                  textRight={t(`common:status.${data.ach_credit_enabled ? 'true' : 'false'}`)}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.virtualAccount.achDebitEnabled')}
                  textRight={t(`common:status.${data.ach_debit_enabled ? 'true' : 'false'}`)}
                />
              </>
            )}
            {data.payment_method_type === 'Card' && (
              <>
                <CustomRow
                  textLeft={t('common:paymentMethods.labels.name')}
                  textRight={data.card_name}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.cards.cardLastFour')}
                  textRight={data.card_last_4 ? `********${data.card_last_4}` : ''}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.cards.expiration')}
                  textRight={
                    data.expiration
                      ? `${data.expiration.slice(-2)}/${data.expiration.substring(0, 4).slice(-2)}`
                      : ''
                  }
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.cards.type')}
                  textRight={data.card_type}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.cards.pullEnabled')}
                  textRight={t(`common:status.${data.pull_enabled ? 'true' : 'false'}`)}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.cards.pushEnabled')}
                  textRight={t(`common:status.${data.push_enabled ? 'true' : 'false'}`)}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.cards.pushAvailability')}
                  textRight={data.push_availability}
                />
                <CustomRow
                  textLeft={t('common:labels.active')}
                  textRight={t(`common:status.${data.active ? 'true' : 'false'}`)}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.cards.country')}
                  textRight={data.country}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.cards.currency')}
                  textRight={data.currency}
                />
                <CustomRow
                  textLeft={t('common:transactions.labels.cards.cardID')}
                  textRight={
                    data.card_id ? `${data.card_id.split('-')[0]}-xxxx-xxxx-xxxx-xxxx` : ''
                  }
                />
              </>
            )}
            <CustomRow
              textLeft={t('common:labels.freezeReasons')}
              textRight={
                Object.values(data.freeze_reasons).length ? (
                  <ul className="list-unstyled mb-0">
                    {Object.entries(data.freeze_reasons).map(([reasonKey, isoTimestamp]) => {
                      const formattedDate = formatDateAndTime(isoTimestamp);
                      return (
                        <li key={reasonKey} className="mb-1">
                          {reasonKey} ({formattedDate})
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  t('common:labels.none')
                )
              }
            />
          </tbody>
        </Table>
      </Modal.Body>
    </Modal>
  );
}

PaymentInstrumentModal.propTypes = {
  /**
   * The visibility boolean
   */
  show: PropTypes.bool.isRequired,
  /**
   * The payment instrument data object
   */
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
  /**
   * The hide function to close the modal
   */
  onHide: PropTypes.func.isRequired,
};

export default PaymentInstrumentModal;
